import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Download, Printer, CheckCircle, AlertTriangle, XCircle,
  Target, AlertCircle
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  ResponsiveContainer, Legend, BarChart, Bar, ReferenceLine
} from 'recharts';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import ReportPDF from '@/components/pdf/ReportPDF';
import { COLOR_SCHEMES, STATUS_COLORS, MILESTONE_STATUSES, RISK_LEVELS } from '@/lib/constants';
import { format } from 'date-fns';
import { SafeResponsiveContainer } from '@/components/SafeChart';

const API = process.env.REACT_APP_BACKEND_URL;

// Status icon helper
const StatusIcon = ({ status }) => {
  switch (status) {
    case 'on_track':
      return <CheckCircle className="h-5 w-5 text-green-600" />;
    case 'at_risk':
      return <AlertTriangle className="h-5 w-5 text-amber-600" />;
    case 'off_track':
      return <XCircle className="h-5 w-5 text-red-600" />;
    default:
      return <Target className="h-5 w-5 text-slate-400" />;
  }
};

// EVM Gauge component
const EvmGauge = ({ value, thresholds = { good: 0.95, ok: 0.85 } }) => {
  const getColor = () => {
    if (value >= thresholds.good) return { stroke: '#22c55e', text: 'text-green-600' };
    if (value >= thresholds.ok) return { stroke: '#f59e0b', text: 'text-amber-600' };
    return { stroke: '#ef4444', text: 'text-red-600' };
  };
  const color = getColor();
  const displayValue = (value * 100).toFixed(0);
  const circumference = 2 * Math.PI * 27;
  const offset = circumference - (circumference * Math.min(value, 1.5) / 1.5);
  
  return (
    <div className="relative w-16 h-16">
      <svg className="transform -rotate-90 w-16 h-16">
        <circle cx="32" cy="32" r="27" stroke="#e2e8f0" strokeWidth="5" fill="none" />
        <circle cx="32" cy="32" r="27" stroke={color.stroke} strokeWidth="5" fill="none" 
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-sm font-bold ${color.text}`}>{displayValue}%</span>
      </div>
    </div>
  );
};

// Metric Circle component
const MetricCircle = ({ value, size = 56, label }) => {
  const circumference = 2 * Math.PI * ((size / 2) - 4);
  const offset = circumference - (circumference * value / 100);
  const color = { stroke: '#3b82f6', text: 'text-blue-600' };
  
  return (
    <div className="text-center">
      <div className="relative mx-auto" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" style={{ width: size, height: size }}>
          <circle cx={size/2} cy={size/2} r={(size/2) - 4} stroke="#e2e8f0" strokeWidth="4" fill="none" />
          <circle cx={size/2} cy={size/2} r={(size/2) - 4} stroke={color.stroke} strokeWidth="4" fill="none" 
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-base font-bold ${color.text}`}>{value}%</span>
        </div>
      </div>
      {label && <div className="text-xs text-slate-500 uppercase mt-1">{label}</div>}
    </div>
  );
};

const PublicReportView = () => {
  const { reportId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [report, setReport] = useState(null);
  const [project, setProject] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pdfReady, setPdfReady] = useState(false);
  const pdfContainerRef = useRef(null);
  
  const colorScheme = 'slate';
  const scheme = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.slate;

  // Guard for reportId - don't fetch if undefined (happens during back navigation)
  useEffect(() => {
    if (!reportId) return;
    loadPublicReport();
  }, [reportId, token]);

  // Delay PDF rendering until container has dimensions
  useEffect(() => {
    if (!report || !project) return;
    
    const checkAndSetReady = () => {
      const container = pdfContainerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setPdfReady(true);
          return true;
        }
      }
      return false;
    };

    // Try immediately
    if (checkAndSetReady()) return;

    // Fallback with RAF and timeout
    const timeout = setTimeout(() => {
      requestAnimationFrame(() => {
        setPdfReady(true);
      });
    }, 150);

    return () => clearTimeout(timeout);
  }, [report, project]);

  const loadPublicReport = async () => {
    try {
      // Pass token as query parameter for security validation
      const url = token 
        ? `${API}/api/public/reports/${reportId}?token=${token}`
        : `${API}/api/public/reports/${reportId}`;
      const response = await axios.get(url);
      setReport(response.data.report);
      setProject(response.data.project);
      setChartData(response.data.chart_data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Report not found or not available for sharing');
    } finally {
      setLoading(false);
    }
  };

  const isPredictive = project?.methodology === 'predictive';
  const showEvm = report?.show_evm !== false;

  const renderSection = (sectionId) => {
    switch (sectionId) {
      case 'header':
        return (
          <div className="p-5 border-b" style={{ backgroundColor: scheme.bg }}>
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-xl font-bold" style={{ color: scheme.primary }}>{project?.name}</h1>
                <p className="text-sm text-slate-500">
                  {report?.period_start && format(new Date(report.period_start), 'MMM d')} - {report?.period_end && format(new Date(report.period_end), 'MMM d, yyyy')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatusIcon status={report?.status} />
                <Badge className={STATUS_COLORS[report?.status] || 'bg-slate-100'}>
                  {report?.status === 'on_track' && 'On Track'}
                  {report?.status === 'at_risk' && 'At Risk'}
                  {report?.status === 'off_track' && 'Off Track'}
                </Badge>
              </div>
            </div>
            {report?.status_summary && (
              <p className="mt-3 text-sm text-slate-600">{report.status_summary}</p>
            )}
          </div>
        );
        
      case 'metrics':
        if (isPredictive) {
          const actualPct = report?.actual_percent || 0;
          const hasEvm = showEvm && report?.evm;
          const spi = report?.evm?.spi;
          const cpi = report?.evm?.cpi;
          
          return (
            <div className="px-5 py-4 border-b">
              <div className="flex items-center justify-center gap-8">
                <MetricCircle value={actualPct} size={56} label="Done" />
                {hasEvm && spi && (
                  <div className="text-center">
                    <EvmGauge value={spi} />
                    <div className="text-xs text-slate-500 uppercase">SPI</div>
                  </div>
                )}
                {hasEvm && cpi && (
                  <div className="text-center">
                    <EvmGauge value={cpi} />
                    <div className="text-xs text-slate-500 uppercase">CPI</div>
                  </div>
                )}
              </div>
              {hasEvm && report?.evm && (
                <div className="grid grid-cols-4 gap-3 mt-4 text-center">
                  <div className="p-2 bg-slate-50 rounded">
                    <div className="text-xs text-slate-500">SPI</div>
                    <div className="font-bold">{spi?.toFixed(2)}</div>
                  </div>
                  <div className="p-2 bg-slate-50 rounded">
                    <div className="text-xs text-slate-500">CPI</div>
                    <div className="font-bold">{cpi?.toFixed(2)}</div>
                  </div>
                  <div className="p-2 bg-slate-50 rounded">
                    <div className="text-xs text-slate-500">EAC</div>
                    <div className="font-bold">${report.evm.eac?.toLocaleString()}</div>
                  </div>
                  <div className="p-2 bg-slate-50 rounded">
                    <div className="text-xs text-slate-500">VAC</div>
                    <div className={`font-bold ${report.evm.vac >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {report.evm.vac >= 0 ? '+' : ''}${report.evm.vac?.toLocaleString()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        } else {
          // Agile metrics
          return (
            <div className="px-5 py-4 border-b">
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">Sprint {report?.sprint_number || '?'}</div>
                  {report?.sprint_goal && (
                    <p className="text-sm text-slate-500">{report.sprint_goal}</p>
                  )}
                </div>
                {report?.points_completed !== undefined && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{report.points_completed}</div>
                    <div className="text-xs text-slate-500">Points Done</div>
                  </div>
                )}
              </div>
            </div>
          );
        }

      case 'chart':
        // S-Curve for predictive projects
        if (isPredictive && chartData?.scurve_data?.length > 0) {
          const dataWithBaseline = chartData.scurve_data.map((d, i, arr) => ({
            ...d,
            bac_baseline: project.bac ? (project.bac * ((i + 1) / arr.length)) : null
          }));

          return (
            <div className="p-5 border-b">
              <h2 className="text-lg font-semibold mb-4" style={{ color: scheme.primary }}>S-Curve</h2>
              <SafeResponsiveContainer height={224}>
                <LineChart data={dataWithBaseline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
                  <RechartsTooltip formatter={(value) => value ? `$${value?.toLocaleString()}` : 'N/A'} />
                  <Legend />
                  <Line type="monotone" dataKey="bac_baseline" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" name="BAC Baseline" dot={false} connectNulls={true} />
                  <Line type="monotone" dataKey="pv" stroke="#3b82f6" name="Planned Value (PV)" strokeWidth={2} connectNulls={true} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="ev" stroke="#22c55e" name="Earned Value (EV)" strokeWidth={2} connectNulls={true} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="ac" stroke="#f97316" name="Actual Cost (AC)" strokeWidth={2} connectNulls={true} dot={{ r: 3 }} />
                </LineChart>
              </SafeResponsiveContainer>
            </div>
          );
        }
        // Velocity chart for agile projects
        else if (!isPredictive && chartData?.velocity_data?.length > 0) {
          return (
            <div className="p-5 border-b">
              <h2 className="text-lg font-semibold mb-4" style={{ color: scheme.primary }}>Velocity Trend</h2>
              <SafeResponsiveContainer height={224}>
                <BarChart data={chartData.velocity_data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="sprint" fontSize={11} tickFormatter={(v) => `S${v}`} />
                  <YAxis fontSize={11} />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="planned" fill="#94a3b8" name="Planned" />
                  <Bar dataKey="completed" fill="#22c55e" name="Completed" />
                </BarChart>
              </SafeResponsiveContainer>
            </div>
          );
        }
        return null;
        
      case 'accomplishments':
        if (!report?.accomplishments?.length) return null;
        return (
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold mb-3" style={{ color: scheme.primary }}>Accomplishments</h2>
            <ul className="space-y-2 ml-4">
              {report.accomplishments.map((item, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span>{item.description}</span>
                    {item.owner && <span className="text-slate-500 ml-2">— {item.owner}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
        
      case 'upcoming':
        if (!report?.upcoming?.length) return null;
        return (
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold mb-3" style={{ color: scheme.primary }}>Upcoming</h2>
            <ul className="space-y-2 ml-4">
              {report.upcoming.map((item, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <Target className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <span>{item.description}</span>
                    {item.owner && <span className="text-slate-500 ml-2">— {item.owner}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );
        
      case 'blockers':
        const activeBlockers = report?.blockers?.filter(b => !b.solved) || [];
        if (!activeBlockers.length) return null;
        return (
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold mb-3" style={{ color: scheme.primary }}>
              Blockers ({activeBlockers.length})
            </h2>
            <ul className="space-y-2 ml-4">
              {activeBlockers.map((item, i) => (
                <li key={i} className="p-3 bg-red-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-sm">{item.description}</span>
                      <div className="flex gap-3 mt-1 text-xs text-slate-500">
                        {item.owner && <span>Owner: {item.owner}</span>}
                        {item.escalated && <Badge variant="outline" className="text-red-600 border-red-200">Escalated</Badge>}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );

      case 'risks':
        const openRisks = project?.risks?.filter(r => r.status === 'open') || [];
        if (!openRisks.length) return null;
        return (
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold mb-3" style={{ color: scheme.primary }}>
              Active Risks ({openRisks.length})
            </h2>
            <div className="space-y-2 ml-4">
              {openRisks.slice(0, 5).map((risk, i) => {
                const probInfo = RISK_LEVELS.find(l => l.value === risk.probability);
                const impactInfo = RISK_LEVELS.find(l => l.value === risk.impact);
                return (
                  <div key={i} className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-medium text-sm">{risk.description}</p>
                    <div className="flex gap-3 mt-2">
                      <Badge className={probInfo?.color} variant="outline">P: {probInfo?.label}</Badge>
                      <Badge className={impactInfo?.color} variant="outline">I: {impactInfo?.label}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'decisions':
        if (!report?.decisions?.length) return null;
        return (
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold mb-3" style={{ color: scheme.primary }}>Decisions Needed</h2>
            <ul className="space-y-2 ml-4">
              {report.decisions.map((item, i) => (
                <li key={i} className="p-3 bg-yellow-50 rounded-lg">
                  <p className="font-medium text-sm">{item.description}</p>
                  <div className="flex gap-3 mt-1 text-xs text-slate-600">
                    {item.needed_by && <span>Needed by: {item.needed_by}</span>}
                    {item.recommendation && <span>Rec: {item.recommendation}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        );

      case 'milestones':
        if (!project?.milestones?.length) return null;
        return (
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold mb-3" style={{ color: scheme.primary }}>
              Milestones ({project.milestones.length})
            </h2>
            <div className="space-y-1 ml-4">
              {project.milestones.slice(0, 6).map((m, i) => {
                const statusInfo = MILESTONE_STATUSES.find(s => s.value === m.status);
                return (
                  <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                    <div className="flex items-center gap-2">
                      {m.status === 'complete' ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Target className="h-4 w-4 text-slate-400" />
                      )}
                      <span>{m.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500">{m.target_date}</span>
                      <Badge className={statusInfo?.color} variant="outline">{statusInfo?.label}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'budget':
        if (!isPredictive || !project?.bac || !report?.pv) return null;
        return (
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold mb-3" style={{ color: scheme.primary }}>Budget Summary</h2>
            <div className="grid grid-cols-3 gap-3 text-center ml-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">PV (Planned)</p>
                <p className="text-lg font-bold">${report.pv?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">EV (Earned)</p>
                <p className="text-lg font-bold">${report.ev?.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">AC (Actual)</p>
                <p className="text-lg font-bold">${report.ac?.toLocaleString()}</p>
              </div>
            </div>
          </div>
        );

      case 'scope':
        if (!project?.scope_summary) return null;
        return (
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold mb-3" style={{ color: scheme.primary }}>Scope</h2>
            <p className="text-slate-600 text-sm ml-4">{project.scope_summary}</p>
          </div>
        );
        
      case 'kudos':
        const kudosArray = Array.isArray(report?.kudos) ? report.kudos : [];
        const kudosString = typeof report?.kudos === 'string' ? report.kudos : '';
        if (!kudosArray.some(k => k.person || k.reason) && !kudosString.trim()) return null;
        return (
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold mb-3" style={{ color: scheme.primary }}>🎉 Kudos</h2>
            {kudosString.trim() ? (
              <p className="text-slate-600 text-sm ml-4">{kudosString}</p>
            ) : (
              <ul className="space-y-2 ml-4">
                {kudosArray.filter(k => k.person || k.reason).map((k, i) => (
                  <li key={i} className="text-sm p-2 bg-amber-50 rounded">
                    <strong>{k.person}</strong>{k.person && k.reason ? ': ' : ''}{k.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
        
      case 'notes':
        if (!report?.notes) return null;
        return (
          <div className="p-5 border-b">
            <h2 className="text-lg font-semibold mb-3" style={{ color: scheme.primary }}>Notes</h2>
            <p className="text-sm text-slate-600 whitespace-pre-wrap ml-4">{report.notes}</p>
          </div>
        );
        
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-64 w-96" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Report Not Available</h2>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show all sections that have content - matching the PDF/print view
  const sectionOrder = ['header', 'metrics', 'chart', 'accomplishments', 'upcoming', 'blockers', 'risks', 'decisions', 'milestones', 'budget', 'scope', 'kudos', 'notes'];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold">Shared Report</h1>
            <p className="text-sm text-slate-500">{project?.name}</p>
          </div>
          <div className="flex gap-2">
            <PDFDownloadLink
              document={
                <ReportPDF
                  report={report}
                  project={project}
                  chartData={chartData}
                  colorScheme={colorScheme || 'slate'}
                  template="standard"
                  showBranding={true}
                  riskFilter="active"
                />
              }
              fileName={`${project?.name?.replace(/\s+/g, '_')}_Report_${report?.report_date || 'draft'}.pdf`}
            >
              {({ loading }) => (
                <Button disabled={loading}>
                  <Download className="h-4 w-4 mr-2" />
                  {loading ? 'Generating...' : 'Export PDF'}
                </Button>
              )}
            </PDFDownloadLink>
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="max-w-5xl mx-auto py-6 px-4">
        <div 
          ref={pdfContainerRef}
          className="bg-white rounded-lg shadow-lg overflow-hidden" 
          style={{ height: 'calc(100vh - 160px)', minHeight: '700px' }}
        >
          {pdfReady ? (
            <PDFViewer width="100%" height="100%" showToolbar={false}>
              <ReportPDF
                report={report}
                project={project}
                chartData={chartData}
                colorScheme={colorScheme || 'slate'}
                template="standard"
                showBranding={true}
                riskFilter="active"
              />
            </PDFViewer>
          ) : (
            <div className="flex items-center justify-center h-full bg-slate-50">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 mx-auto mb-3"></div>
                <p className="text-sm text-slate-500">Preparing preview...</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicReportView;
